import { Statistic, Card, Row, Col, Icon, Tooltip, message } from 'antd';
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import FileDownload from 'js-file-download';
import moment from 'moment';

import { request } from '~/_shared/instance';

import { LoadingTopBar } from "~/components/UI/LoadingTopBar";
import { Layout } from "~/components/UI";
import {
    ModuleHeaderWrapper,
    ModuleHeader
} from '~/components/ModuleHeader';
import Search from '~/_shared/components/Search';
import SelectionToolbar from '~/_shared/components/SelectionToolbar';

import TableList from './TableList';
import { loadPartnersReport } from '../actions';

var Partners = function() {
    var dispatch = useDispatch();
    var partners = useSelector(function(state) { return state.reports.partners.data; });
    var isLoading = useSelector(function(state) { return state.reports.partners.isLoading; });

    var _searchState = useState('');
    var searchText = _searchState[0];
    var setSearchText = _searchState[1];

    var _selectedState = useState([]);
    var selectedRowKeys = _selectedState[0];
    var setSelectedRowKeys = _selectedState[1];

    var _exportingState = useState(false);
    var isExporting = _exportingState[0];
    var setIsExporting = _exportingState[1];

    useEffect(function() {
        dispatch(loadPartnersReport());
    }, []);

    var handleSearchChange = useCallback(function(e) {
        setSearchText(e.target.value);
    }, []);

    var handleClearSelection = useCallback(function() {
        setSelectedRowKeys([]);
    }, []);

    var handleExportToExcel = useCallback(function() {
        setIsExporting(true);
        request(
            'POST',
            '/reports/partners/export/excel',
            { ids: selectedRowKeys },
            true,
            { responseType: 'blob' }
        )
        .then(function(response) {
            var now = moment().format('DD_MM_YYYY_HH-mm-ss');
            FileDownload(response.data, 'Socios_' + now + '.xlsx');
        })
        .catch(function() {
            message.error('Não foi possível exportar para Excel!');
        })
        .finally(function() {
            setIsExporting(false);
        });
    }, [selectedRowKeys]);

    // Single-pass filtering with for loop
    var filteredData = useMemo(function() {
        if (!searchText) return partners;
        var lower = searchText.toLowerCase();
        var result = [];
        for (var i = 0, len = partners.length; i < len; i++) {
            var item = partners[i];
            if (
                (item.customer_name && item.customer_name.toLowerCase().indexOf(lower) !== -1) ||
                (item.customer_cod && String(item.customer_cod).toLowerCase().indexOf(lower) !== -1) ||
                (item.customer_cnpj && item.customer_cnpj.toLowerCase().indexOf(lower) !== -1) ||
                (item.partner_name && item.partner_name.toLowerCase().indexOf(lower) !== -1) ||
                (item.partner_cpf && item.partner_cpf.toLowerCase().indexOf(lower) !== -1) ||
                (item.partner_phone && item.partner_phone.toLowerCase().indexOf(lower) !== -1)
            ) {
                result.push(item);
            }
        }
        return result;
    }, [partners, searchText]);

    var stats = useMemo(function() {
        var seen = {};
        var rlCount = 0;
        var withPhone = 0;
        var withCpf = 0;
        var withoutPhone = 0;
        var withoutCpf = 0;

        for (var i = 0, len = filteredData.length; i < len; i++) {
            var item = filteredData[i];
            seen[item.customer_cod] = true;
            if (item.partner_is_legal_representative) rlCount++;
            if (item.partner_phone) { withPhone++; } else { withoutPhone++; }
            if (item.partner_cpf) { withCpf++; } else { withoutCpf++; }
        }

        var companies = Object.keys(seen).length;
        var avg = companies > 0 ? (filteredData.length / companies).toFixed(1) : 0;

        return {
            total: filteredData.length,
            companies: companies,
            rlCount: rlCount,
            withPhone: withPhone,
            withoutPhone: withoutPhone,
            withCpf: withCpf,
            withoutCpf: withoutCpf,
            avgPerCompany: avg
        };
    }, [filteredData]);

    var tipRow = function(label, value, opts) {
        var color = (opts && opts.color) || '#fff';
        var bold = opts && opts.bold;
        var borderTop = opts && opts.borderTop;
        return React.createElement('div', {
            key: label,
            style: {
                display: 'flex', justifyContent: 'space-between', gap: '16px',
                padding: '3px 0',
                fontWeight: bold ? 600 : 400,
                borderTop: borderTop ? '1px solid rgba(255,255,255,0.15)' : 'none',
                marginTop: borderTop ? '4px' : 0,
                paddingTop: borderTop ? '6px' : '3px'
            }
        },
            React.createElement('span', { style: { color: 'rgba(255,255,255,0.7)' } }, label),
            React.createElement('span', { style: { color: color } }, value)
        );
    };

    var tipTitle = function(text) {
        return React.createElement('div', {
            key: 'title-' + text,
            style: { fontWeight: 700, fontSize: '12px', marginBottom: '6px', color: '#fff', letterSpacing: '0.02em' }
        }, text);
    };

    var tipContainer = function(children) {
        return React.createElement('div', { style: { minWidth: '200px', fontSize: '12px' } }, children);
    };

    var sociosTooltip = tipContainer([
        tipTitle('Detalhes dos Sócios'),
        tipRow('Total de sócios', stats.total + ''),
        tipRow('Representantes legais', stats.rlCount + '', { color: '#69c0ff' }),
        tipRow('Com WhatsApp', stats.withPhone + '', { color: '#95de64' }),
        tipRow('Sem WhatsApp', stats.withoutPhone + '', { color: stats.withoutPhone > 0 ? '#ff7875' : '#95de64' }),
        tipRow('Com CPF', stats.withCpf + '', { color: '#95de64' }),
        tipRow('Sem CPF', stats.withoutCpf + '', { color: stats.withoutCpf > 0 ? '#ff7875' : '#95de64' })
    ]);

    var empresasTooltip = tipContainer([
        tipTitle('Detalhes das Empresas'),
        tipRow('Total de empresas', stats.companies + ''),
        tipRow('Média de sócios/empresa', stats.avgPerCompany + ''),
        tipRow('Total de sócios', stats.total + '', { borderTop: true })
    ]);

    var rowSelection = useMemo(function() {
        return {
            selectedRowKeys: selectedRowKeys,
            onChange: setSelectedRowKeys
        };
    }, [selectedRowKeys]);

    return (
        <>
            <LoadingTopBar isActive={isLoading} />
            <Layout>
                <div>
                    <ModuleHeaderWrapper>
                        <ModuleHeader
                            breadcrumbs={['Relatórios', 'Sócios']}
                            title="Quadro de Sócios"
                            search={
                                <Search
                                    placeholder="Buscar por empresa, sócio, CPF, CNPJ..."
                                    value={searchText}
                                    onChange={handleSearchChange}
                                    resultsCount={searchText ? filteredData.length : undefined}
                                />
                            }
                        />
                    </ModuleHeaderWrapper>
                </div>
                <Row gutter={16}>
                    <Col span={12}>
                        <Tooltip title={sociosTooltip} placement="bottom">
                            <Card style={{ marginBottom: 10 }} bodyStyle={{ padding: '15px' }}>
                                <Statistic
                                    title={
                                        <span>
                                            <Icon type="team" style={{ marginRight: 6 }} />
                                            Total de Sócios
                                        </span>
                                    }
                                    value={stats.total}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Tooltip>
                    </Col>
                    <Col span={12}>
                        <Tooltip title={empresasTooltip} placement="bottom">
                            <Card style={{ marginBottom: 10 }} bodyStyle={{ padding: '15px' }}>
                                <Statistic
                                    title={
                                        <span>
                                            <Icon type="shop" style={{ marginRight: 6 }} />
                                            Empresas
                                        </span>
                                    }
                                    value={stats.companies}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Tooltip>
                    </Col>
                </Row>

                <TableList
                    filteredData={filteredData}
                    isLoading={isLoading}
                    rowSelection={rowSelection}
                />

                <SelectionToolbar
                    count={selectedRowKeys.length}
                    onClear={handleClearSelection}
                    itemLabel="sócio"
                    actions={[
                        { label: 'Exportar Excel', icon: 'download', onClick: handleExportToExcel, loading: isExporting }
                    ]}
                />
            </Layout>
        </>
    );
};

export default Partners;
